/**
 * Super Admin — permanently delete a user.
 *
 * The copy here is the feature. A delete cascades workspace access away and
 * sets audit_logs.user_id to null, so the record of what the account approved
 * or changed survives but stops naming anyone — verified against the database.
 * Asking "are you sure?" would hide exactly the part people do not expect, so
 * the modal says it and asks for the email in return.
 *
 * The master case is refused here too, but the real guard is in the route: this
 * is a courtesy, not a control.
 *
 * @module components/admin/DeleteUserModal
 */

import React, { useState } from "react";
import { Modal } from "../Modal";
import { Button } from "../Button";
import { deleteUser, type AdminUser } from "../../services/adminService";

export interface DeleteUserModalProps {
  isOpen: boolean;
  user: AdminUser | null;
  isMaster: boolean;
  onClose: () => void;
  onDeleted: () => Promise<void> | void;
}

export const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  isOpen,
  user,
  isMaster,
  onClose,
  onDeleted,
}) => {
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const handleClose = () => {
    if (busy) return;
    setConfirmText("");
    setError(null);
    onClose();
  };

  // Exact match, not case-insensitive: the point is to make the reader look at
  // which account this is, and a delete is not the place to be helpful.
  const matches = confirmText.trim() === user.email;

  const handleDelete = async () => {
    setError(null);
    setBusy(true);
    const result = await deleteUser(user.id);
    if (!result.success) {
      setError(result.error ?? "Could not delete the user");
      setBusy(false);
      return;
    }
    await onDeleted();
    setBusy(false);
    setConfirmText("");
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Delete user: ${user.email}`}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          {!isMaster && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => void handleDelete()}
              disabled={!matches || busy}
              loading={busy}
            >
              Delete forever
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-3">
        {isMaster ? (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-md px-3 py-2 text-sm">
            The platform master cannot be deleted. Deleting it would remove the
            master role itself, and there is no way to appoint a new one from
            inside the app.
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-md px-3 py-2 text-sm">
                {error}
              </div>
            )}

            <p className="text-sm font-medium text-red-700">
              Permanent. This cannot be undone.
            </p>

            <ul className="text-sm text-neutral-700 list-disc pl-5 space-y-1">
              <li>
                Workspace access is removed immediately
                {user.workspaces.length > 0 && (
                  <> ({user.workspaces.map((w) => `${w.name} · ${w.role}`).join(", ")})</>
                )}
              </li>
              <li>
                Their audit history stays, but will no longer say who performed
                each action
              </li>
              <li>Coming back means creating a new account</li>
            </ul>

            <label className="block text-sm">
              <span className="text-neutral-700">
                Type {user.email} to confirm
              </span>
              <input
                type="text"
                aria-label={`Type ${user.email} to confirm`}
                value={confirmText}
                disabled={busy}
                onChange={(e) => setConfirmText(e.target.value)}
                className="mt-1 w-full text-sm border border-neutral-300 rounded-md px-3 py-1.5"
              />
            </label>
          </>
        )}
      </div>
    </Modal>
  );
};

export default DeleteUserModal;
